﻿import { useState, useCallback } from "react";
import { calibrateAssessment, compareAssessment } from "../api/client";
import type { AssessmentLevel, AssessmentQuestion, OccupationDetail, Answer, CalibrateResponse, CompareResponse } from "../types";

export interface AssessmentState {
  level: AssessmentLevel;
  occupationId: number | null;
  occupationDetail: OccupationDetail | null;
  questions: AssessmentQuestion[];
  answers: Answer[];
  result: CalibrateResponse | null;
  aiCompare: CompareResponse | null;
  aiLoading: boolean;
}

const STORAGE_KEY = "careerai_assessment";

const initialState: AssessmentState = {
  level: 1,
  occupationId: null,
  occupationDetail: null,
  questions: [],
  answers: [],
  result: null,
  aiCompare: null,
  aiLoading: false,
};

function loadState(): AssessmentState {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? { ...initialState, ...JSON.parse(stored) } : initialState;
  } catch {
    return initialState;
  }
}

function saveState(state: AssessmentState) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function useAssessment() {
  const [state, setState] = useState<AssessmentState>(loadState);

  const selectOccupation = useCallback((detail: OccupationDetail) => {
    const next: AssessmentState = {
      ...initialState,
      level: 1,
      occupationId: detail.id,
      occupationDetail: detail,
    };
    saveState(next);
    setState(next);
  }, []);

  const startQuestionnaire = useCallback(() => {
    setState((prev) => {
      const next = { ...prev, level: 2 as AssessmentLevel };
      saveState(next);
      return next;
    });
  }, []);

  const submitAnswers = useCallback(
    async (allAnswers: Answer[]) => {
      if (!state.occupationId) return;

      const res = await calibrateAssessment({
        occupation_id: state.occupationId,
        answers: allAnswers,
      });

      if (res.success && res.data) {
        const next: AssessmentState = {
          ...state,
          level: 3,
          answers: allAnswers,
          result: res.data,
        };
        saveState(next);
        setState(next);
      }
    },
    [state]
  );

  const fetchAICompare = useCallback(async () => {
    if (!state.occupationId) return;

    setState((prev) => ({ ...prev, aiLoading: true }));
    const aiRes = await compareAssessment({
      occupation_id: state.occupationId,
      answers: state.answers,
    });
    setState((prev) => ({
      ...prev,
      aiLoading: false,
      aiCompare: aiRes.success ? aiRes.data : null,
    }));
  }, [state.occupationId, state.answers]);

  const reset = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setState(initialState);
  }, []);

  return { state, selectOccupation, startQuestionnaire, submitAnswers, fetchAICompare, reset };
}
